import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const TransactionHistory = ({ transactions }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');

  const filteredTransactions = transactions
    .filter(tx => {
      const matchesSearch = tx.symbol.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = filterType === 'all' || tx.type === filterType;
      return matchesSearch && matchesType;
    })
    .sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'symbol':
          aValue = a.symbol;
          bValue = b.symbol;
          break;
        case 'type':
          aValue = a.type;
          bValue = b.type;
          break;
        case 'quantity':
          aValue = a.quantity;
          bValue = b.quantity;
          break;
        case 'total':
          aValue = a.total;
          bValue = b.total;
          break;
        default:
          aValue = a.timestamp;
          bValue = b.timestamp;
      }

      if (typeof aValue === 'string') {
        return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    });

  const formatCurrency = (value) => {
    return value.toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  const formatCrypto = (value) => {
    return value.toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 6 
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success bg-success-50';
      case 'pending': return 'text-warning bg-warning-50';
      case 'failed': return 'text-error bg-error-50';
      default: return 'text-text-secondary bg-secondary-50';
    }
  };

  const getTypeIcon = (type) => {
    return type === 'buy' ? 'ArrowDownLeft' : 'ArrowUpRight';
  };

  const getTypeColor = (type) => {
    return type === 'buy' ? 'text-success' : 'text-error';
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-surface border border-border rounded-trading p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
            {/* Search */}
            <div className="relative">
              <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary" />
              <input
                type="text"
                placeholder="Search by asset..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary text-sm"
              />
            </div>

            {/* Filter by Type */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary text-sm"
            >
              <option value="all">All Transactions</option>
              <option value="buy">Buy Orders</option>
              <option value="sell">Sell Orders</option>
            </select>
          </div>

          <div className="flex items-center space-x-4">
            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary text-sm"
            >
              <option value="timestamp">Sort by Date</option>
              <option value="symbol">Sort by Asset</option>
              <option value="type">Sort by Type</option>
              <option value="quantity">Sort by Quantity</option>
              <option value="total">Sort by Total</option>
            </select>

            {/* Sort Order */}
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-2 border border-border rounded-trading hover:bg-secondary-50 transition-colors duration-trading-fast"
            >
              <Icon 
                name={sortOrder === 'asc' ? 'ArrowUp' : 'ArrowDown'} 
                size={16} 
                className="text-text-secondary" 
              />
            </button>
          </div>
        </div>
      </div>

      {/* Transaction Table */}
      <div className="bg-surface border border-border rounded-trading overflow-hidden">
        <div className="p-4 border-b border-border">
          <h3 className="text-lg font-heading-semibold text-text-primary">
            Transaction History
          </h3>
        </div>

        {/* Desktop Table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary-50">
              <tr>
                <th className="text-left px-4 py-3 font-body-medium text-sm text-text-secondary">Type</th>
                <th className="text-left px-4 py-3 font-body-medium text-sm text-text-secondary">Asset</th>
                <th className="text-right px-4 py-3 font-body-medium text-sm text-text-secondary">Quantity</th>
                <th className="text-right px-4 py-3 font-body-medium text-sm text-text-secondary">Price</th>
                <th className="text-right px-4 py-3 font-body-medium text-sm text-text-secondary">Total</th>
                <th className="text-right px-4 py-3 font-body-medium text-sm text-text-secondary">Fee</th>
                <th className="text-left px-4 py-3 font-body-medium text-sm text-text-secondary">Date</th>
                <th className="text-center px-4 py-3 font-body-medium text-sm text-text-secondary">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.map((transaction) => (
                <tr key={transaction.id} className="border-b border-border hover:bg-secondary-50 transition-colors duration-trading-fast">
                  <td className="px-4 py-4">
                    <div className={`flex items-center space-x-2 ${getTypeColor(transaction.type)}`}>
                      <Icon name={getTypeIcon(transaction.type)} size={16} />
                      <span className="font-body-medium text-sm capitalize">
                        {transaction.type}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="font-body-medium text-sm text-text-primary">
                      {transaction.symbol}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <div className="font-data text-sm text-text-primary">
                      {formatCrypto(transaction.quantity)}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <div className="font-data text-sm text-text-primary">
                      ${formatCurrency(transaction.price)}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <div className="font-data text-sm text-text-primary">
                      ${formatCurrency(transaction.total)}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <div className="font-data text-sm text-text-secondary">
                      ${formatCurrency(transaction.fee)}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="font-caption-normal text-sm text-text-secondary">
                      {transaction.timestamp.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </div>
                    <div className="font-caption-normal text-xs text-text-secondary">
                      {transaction.timestamp.toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className={`px-2 py-1 text-xs font-body-medium rounded-trading ${getStatusColor(transaction.status)}`}>
                      {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden divide-y divide-border">
          {filteredTransactions.map((transaction) => (
            <div key={transaction.id} className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className={`flex items-center space-x-2 ${getTypeColor(transaction.type)}`}>
                  <Icon name={getTypeIcon(transaction.type)} size={18} />
                  <span className="font-body-medium text-text-primary capitalize">
                    {transaction.type} {transaction.symbol}
                  </span>
                </div>
                
                <span className={`px-2 py-1 text-xs font-body-medium rounded-trading ${getStatusColor(transaction.status)}`}>
                  {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <div className="font-caption-normal text-xs text-text-secondary mb-1">Quantity</div>
                  <div className="font-data text-sm text-text-primary">
                    {formatCrypto(transaction.quantity)}
                  </div>
                </div>
                <div>
                  <div className="font-caption-normal text-xs text-text-secondary mb-1">Price</div>
                  <div className="font-data text-sm text-text-primary">
                    ${formatCurrency(transaction.price)}
                  </div>
                </div>
                <div>
                  <div className="font-caption-normal text-xs text-text-secondary mb-1">Total</div>
                  <div className="font-data text-sm text-text-primary">
                    ${formatCurrency(transaction.total)}
                  </div>
                </div>
                <div>
                  <div className="font-caption-normal text-xs text-text-secondary mb-1">Fee</div>
                  <div className="font-data text-sm text-text-secondary">
                    ${formatCurrency(transaction.fee)}
                  </div>
                </div>
              </div>

              <div className="text-right">
                <div className="font-caption-normal text-xs text-text-secondary">
                  {transaction.timestamp.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })} at {transaction.timestamp.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredTransactions.length === 0 && (
          <div className="p-8 text-center">
            <Icon name="FileText" size={48} className="text-text-secondary mx-auto mb-4" />
            <h3 className="text-lg font-heading-semibold text-text-primary mb-2">
              No Transactions Found
            </h3>
            <p className="text-text-secondary font-body-normal">
              {searchTerm || filterType !== 'all' ?'Try adjusting your search or filter criteria.' :'Your transaction history will appear here once you start trading.'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionHistory;